import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Plus, Heart, MessageCircle, Share2, Users, Award, TrendingUp, Search, Filter, Paperclip, Link2, BarChart3, Smile, UserPlus, Video, FileText, Upload, Eye, Settings, Trash2, Reply, Pin, Edit3, Check, X, Star, Image, Camera, AlertTriangle, Loader2, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const Community = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [newPostText, setNewPostText] = useState('');
  const [showPostOptions, setShowPostOptions] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [postAttachment, setPostAttachment] = useState(null);
  const [postLink, setPostLink] = useState('');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showPollModal, setShowPollModal] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedPostForReply, setSelectedPostForReply] = useState(null);
  const [newReply, setNewReply] = useState('');
  const [showRepliesModal, setShowRepliesModal] = useState(false);
  const [selectedPostReplies, setSelectedPostReplies] = useState(null);
  const [postReplies, setPostReplies] = useState([]);
  
  // New features states
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [replyImage, setReplyImage] = useState(null);
  const [replyImagePreview, setReplyImagePreview] = useState(null);
  const [pinnedPosts, setPinnedPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState(null);
  // Training modal removed
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postComments, setPostComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  
  // Real-time community stats (start with base numbers, increment as users join)
  const [communityStats, setCommunityStats] = useState({
    members: 0,
    postsToday: 0,
    activeNow: 0,
    totalSavings: 0,
    successfulProjects: 0,
    successRate: 0
  });

  const postCategories = [
    'General Discussion',
    'Tech Support', 
    'Assignments',
    'Project Submission'
  ];

  const emojis = [
    '😊', '😎', '🤔', '💡', '🎯', '🚀', '✅', '❌', '🔥', '💼', '📊', '📈', '📉', 
    '🎉', '👍', '👎', '❤️', '💪', '🙏', '👏', '⭐', '🏆', '🎖️', '💎', '⚡',
    '💰', '💸', '📱', '💻', '📧', '📞', '📅', '⏰', '🎓', '🔧', '⚙️', '🔍',
    '📝', '📋', '📌', '🔖', '💭', '💬', '🗣️', '🎤', '📢', '📣', '🔔', '🎪'
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Check for logged-in user from localStorage
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setCurrentUser(userData);
        setIsLoggedIn(true);
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        localStorage.removeItem('currentUser');
      }
    }
    
    // Load initial data
    fetchCommunityStats();
    fetchCommunityPosts();
  }, []);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const replyTextareaRef = useRef(null);
  const commentTextareaRef = useRef(null);
  const newPostTextareaRef = useRef(null);
  
  // Simple state to track if textareas have content (for button enabling only)
  const [hasReplyContent, setHasReplyContent] = useState(false);
  const [hasCommentContent, setHasCommentContent] = useState(false);
  const [hasPostContent, setHasPostContent] = useState(false);

  // API Functions
  const fetchCommunityStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/community/stats`);
      setCommunityStats(response.data || {
        members: 0,
        postsToday: 0,
        activeNow: 0,
        totalSavings: 0,
        successfulProjects: 0,
        successRate: 0
      });
    } catch (error) {
      console.error('Failed to fetch community stats:', error);
      // Set default values on error
      setCommunityStats({
        members: 0,
        postsToday: 0,
        activeNow: 0,
        totalSavings: 0,
        successfulProjects: 0,
        successRate: 0
      });
    }
  };

  const fetchCommunityPosts = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/community/posts`);
      const posts = response.data.posts || [];
      setCommunityPosts(posts);
      
      // Update pinned posts from backend data
      const pinned = posts.filter(post => post.is_pinned).map(post => post.id);
      setPinnedPosts(pinned);
      
      // Fetch likes for each main post (not replies) to show user interactions
      posts.filter(post => !post.parent_post_id).forEach(post => {
        fetchPostLikes(post.id);
      });
    } catch (error) {
      console.error('Failed to fetch community posts:', error);
      setCommunityPosts([]);
    }
  };

  // Real posts will be loaded from backend - starting with empty array
  const [communityPosts, setCommunityPosts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Add new post function
  const [isPosting, setIsPosting] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [selectedPostToFlag, setSelectedPostToFlag] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [flagDetails, setFlagDetails] = useState('');
  
  const handleFlagPost = async () => {
    if (!flagReason) {
      alert('Please select a reason for flagging this post');
      return;
    }

    try {
      await axios.post(`${API_BASE}/api/community/posts/${selectedPostToFlag.id}/flag`, {
        reason: flagReason,
        details: flagDetails
      }, {
        params: { user_id: currentUser?.id }
      });
      
      alert('Post flagged for review. Thank you for helping keep our community safe!');
      setShowFlagModal(false);
      setSelectedPostToFlag(null);
      setFlagReason('');
      setFlagDetails('');
    } catch (error) {
      console.error('Error flagging post:', error);
      alert('Failed to flag post');
    }
  };

  // Content validation function
  const validatePostContent = async (content, image) => {
    try {
      setIsValidating(true);
      setValidationStatus(null);
      
      // Extract links from content
      const linkRegex = /https?:\/\/[^\s]+/g;
      const links = content.match(linkRegex) || [];
      
      // Prepare validation request
      const validationRequest = {
        text_content: content,
        image_data: image, // Base64 image data
        links: links
      };
      
      const response = await axios.post(`${API_BASE}/api/content/validate`, validationRequest);
      
      setValidationStatus(response.data);
      return response.data;
    } catch (error) {
      console.error('Content validation failed:', error);
      // If validation service fails, allow posting but log the error
      const fallbackResult = { 
        is_valid: true, // Allow posting if validation service is down
        violations: [], 
        message: 'Validation service temporarily unavailable' 
      };
      setValidationStatus(fallbackResult);
      return fallbackResult;
    } finally {
      setIsValidating(false);
    }
  };

  const handleCreatePost = async () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    if (isPosting) {
      return; // Prevent double submission
    }
    
    const postText = newPostTextareaRef.current?.value || '';
    
    if (!postText.trim()) {
      alert('Please enter some content for your post');
      return;
    }
    
    try {
      setIsPosting(true);
      
      // Step 1: Validate content before posting
      const validationResult = await validatePostContent(postText, imagePreview);
      
      if (!validationResult.is_valid) {
        // Show validation errors and block posting
        const errorMessage = `Content violates community guidelines:\n\n${validationResult.violations.join('\n')}`;
        alert(errorMessage);
        setIsPosting(false);
        return;
      }
      
      const postData = {
        title: 'Community Post', // Generic title, don't use content as title
        content: postText,
        category: selectedCategory || 'General',
        image: imagePreview || null  // Include image data
      };
      
      await axios.post(`${API_BASE}/api/community/posts`, postData, {
        params: { user_id: currentUser?.id || 'anonymous' }
      });
      
      // Clear all post creation states
      if (newPostTextareaRef.current) {
        newPostTextareaRef.current.value = '';
      }
      setHasPostContent(false);
      setSelectedCategory('General');
      setShowPostOptions(false);
      setSelectedImage(null);
      setImagePreview(null);
      
      // Clear file input
      const fileInput = document.getElementById('image-upload');
      if (fileInput) {
        fileInput.value = '';
      }
      
      await fetchCommunityPosts();
      alert('Post created successfully!');
    } catch (error) {
      console.error('Failed to create post:', error);
      alert('Failed to create post. Please try again.');
    } finally {
      setIsPosting(false);
    }
  };

  // Authentication now handled by separate Login/Signup pages

  const handleLikePost = async (postId) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    try {
      const response = await axios.post(`${API_BASE}/api/community/posts/${postId}/like`, null, {
        params: { user_id: currentUser?.id }
      });
      
      // Update local state immediately for better UX
      setCommunityPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId 
            ? { ...post, likes: response.data.likes, user_liked: response.data.user_liked }
            : post
        )
      );
      
      // Refresh posts to get updated data
      await fetchCommunityPosts();
    } catch (error) {
      console.error('Failed to like post:', error);
      alert('Failed to like post. Please try again.');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!currentUser || currentUser.role !== 'admin') {
      alert('Only administrators can delete posts');
      return;
    }
    
    const confirmDelete = window.confirm('Are you sure you want to delete this post? This action cannot be undone.');
    if (!confirmDelete) {
      return;
    }
    
    try {
      await axios.delete(`${API_BASE}/api/community/posts/${postId}`);
      await fetchCommunityPosts(); // Refresh posts
      alert('Post deleted successfully');
    } catch (error) {
      console.error('Failed to delete post:', error);
      alert('Failed to delete post. Please try again.');
    }
  };

  const handleReplyToPost = (post) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    setSelectedPostForReply(post);
    setNewReply(''); // Clear any existing reply text
    setReplyImage(null); // Clear any existing reply image
    setReplyImagePreview(null);
    setShowReplyModal(true);
  };

  const handleSubmitReply = async () => {
    const replyText = replyTextareaRef.current?.value || '';
    
    if (!replyText.trim() || !selectedPostForReply) {
      return;
    }
    
    try {
      // Create a reply as a new post with a parent reference
      const replyData = {
        title: 'Reply',
        content: `@${selectedPostForReply.author_name || selectedPostForReply.author} ${replyText}`,
        parent_post_id: selectedPostForReply.id,
        parent_author: selectedPostForReply.author_name || selectedPostForReply.author,
        image: replyImagePreview || null  // Include reply image data
      };
      
      await axios.post(`${API_BASE}/api/community/posts`, replyData, {
        params: { user_id: currentUser?.id || 'anonymous' }
      });
      
      // Store post reference before clearing
      const postToRefresh = selectedPostForReply;
      
      // Clear reply text, image and close modal
      if (replyTextareaRef.current) {
        replyTextareaRef.current.value = '';
      }
      setHasReplyContent(false);
      setReplyImage(null);
      setReplyImagePreview(null);
      
      // Close reply modal
      setShowReplyModal(false);
      setSelectedPostForReply(null);
      
      // Refresh the posts to show new reply
      await fetchCommunityPosts();
      
      // Refresh replies modal if still open
      if (selectedPost) {
        const response = await axios.get(`${API_BASE}/api/community/posts`);
        const allPosts = response.data.posts || [];
        const replies = allPosts.filter(p => p.parent_post_id === selectedPost.id);
        setPostReplies(replies);
      }
      
      alert('Reply posted successfully!');
    } catch (error) {
      console.error('Failed to post reply:', error);
      alert('Failed to post reply. Please try again.');
    }
  };

  const handleShowAllReplies = async (post) => {
    try {
      setSelectedPostReplies(post);
      
      // Fetch all replies for this post (posts with parent_post_id)
      const response = await axios.get(`${API_BASE}/api/community/posts`);
      const allPosts = response.data.posts || [];
      
      // Filter replies for this specific post
      const replies = allPosts.filter(p => p.parent_post_id === post.id);
      setPostReplies(replies);
      
      // Fetch likes for all replies
      replies.forEach(reply => {
        fetchPostLikes(reply.id);
      });
      
      // Also fetch likes for the parent post
      fetchPostLikes(post.id);
      
      setShowRepliesModal(true);
    } catch (error) {
      console.error('Failed to fetch replies:', error);
      alert('Failed to load replies. Please try again.');
    }
  };

  const handleLikeReply = async (replyId) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    try {
      await axios.post(`${API_BASE}/api/community/posts/${replyId}/like`, null, {
        params: { user_id: currentUser?.id }
      });
      // Refresh replies
      await handleShowAllReplies(selectedPostReplies);
      // Also refresh the post likes data
      fetchPostLikes(replyId);
    } catch (error) {
      console.error('Failed to like reply:', error);
    }
  };

  const handleReplyToReply = (reply) => {
    setSelectedPostForReply(reply);
    setShowRepliesModal(false); // Close replies modal
    setShowReplyModal(true); // Open reply modal
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReplyImageUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setReplyImage(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setReplyImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeReplyImagePreview = () => {
    setReplyImage(null);
    setReplyImagePreview(null);
  };

  const removeImagePreview = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handlePinPost = async (postId, postAuthorId) => {
    if (!currentUser) {
      alert('Please log in to pin posts');
      return;
    }
    
    // Users can pin their own posts, admins can pin any post
    if (currentUser.id !== postAuthorId && currentUser.role !== 'admin') {
      alert('You can only pin your own posts');
      return;
    }
    
    try {
      const response = await axios.post(`${API_BASE}/api/community/posts/${postId}/pin`, null, {
        params: { user_id: currentUser.id }
      });
      
      // Update local state based on API response
      if (response.data.is_pinned) {
        setPinnedPosts(prev => [...prev, postId]);
      } else {
        setPinnedPosts(prev => prev.filter(id => id !== postId));
      }
      
      // Refresh community posts and stats
      await fetchCommunityPosts();
      await fetchCommunityStats();
      
      alert(response.data.message);
    } catch (error) {
      console.error('Failed to pin/unpin post:', error);
      alert('Failed to update pin status. Please try again.');
    }
  };

  const handleEditPost = (post) => {
    if (!currentUser || currentUser.id !== post.author_id) {
      alert('You can only edit your own posts');
      return;
    }
    
    setEditingPost(post);
    setEditContent(post.content);
  };

  const handleSaveEdit = async () => {
    if (!editContent.trim() || !editingPost) {
      return;
    }
    
    try {
      // TODO: Implement backend API for editing posts
      // For now, just update locally
      const updatedPosts = communityPosts.map(post => 
        post.id === editingPost.id 
          ? { ...post, content: editContent, updated_at: new Date().toISOString() }
          : post
      );
      setCommunityPosts(updatedPosts);
      
      setEditingPost(null);
      setEditContent('');
      alert('Post updated successfully');
    } catch (error) {
      console.error('Failed to update post:', error);
    }
  };

  const handleDeleteOwnPost = async (postId) => {
    const post = communityPosts.find(p => p.id === postId);
    if (!currentUser || (currentUser.id !== post?.author_id && currentUser.role !== 'admin')) {
      alert('You can only delete your own posts');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_BASE}/api/community/posts/${postId}`);
      await fetchCommunityPosts();
      alert('Post deleted successfully');
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  const handleMarkBestAnswer = async (replyId, postId) => {
    const post = communityPosts.find(p => p.id === postId);
    if (!currentUser || currentUser.id !== post?.author_id) {
      alert('Only the post owner can mark best answers');
      return;
    }
    
    try {
      await axios.post(`${API_BASE}/api/community/posts/${postId}/mark-best-answer`, null, {
        params: { 
          reply_id: replyId,
          user_id: currentUser.id
        }
      });
      
      // Update local state
      const updatedReplies = postReplies.map(reply => 
        reply.id === replyId 
          ? { ...reply, is_best_answer: !reply.is_best_answer }
          : { ...reply, is_best_answer: false } // Only one best answer per post
      );
      setPostReplies(updatedReplies);
      
      // Refresh community posts and stats
      await fetchCommunityPosts();
      await fetchCommunityStats();
      
      alert('Best answer marked successfully');
    } catch (error) {
      console.error('Failed to mark best answer:', error);
      alert('Failed to mark best answer. Please try again.');
    }
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const postDate = new Date(dateString);
    const diffInMinutes = Math.floor((now - postDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getLastCommentTime = (post) => {
    const replies = communityPosts.filter(p => p.parent_post_id === post.id);
    if (replies.length === 0) return null;
    
    const latestReply = replies.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    return `Last comment ${getTimeAgo(latestReply.created_at)}`;
  };

  const [postLikes, setPostLikes] = useState({});
  
  const fetchPostLikes = async (postId) => {
    try {
      const response = await axios.get(`${API_BASE}/api/community/posts/${postId}/likes`);
      setPostLikes(prev => ({ ...prev, [postId]: response.data.likes }));
    } catch (error) {
      console.error('Failed to fetch post likes:', error);
    }
  };

  const UserInteractionAvatars = ({ post }) => {
    const likes = postLikes[post.id] || [];
    
    if (likes.length === 0) return null;

    return (
      <div className="flex items-center gap-2">
        <div className="flex -space-x-1">
          {/* Show liked users */}
          {likes.slice(0, 4).map((like, i) => (
            <div 
              key={like.user_id}
              className="w-6 h-6 bg-gradient-to-br from-red-500 to-pink-500 rounded-full border-2 border-gray-900 flex items-center justify-center text-white text-xs font-semibold"
              title={like.user_name}
            >
              {like.initials}
            </div>
          ))}
          
          {likes.length > 4 && (
            <div className="w-6 h-6 bg-gray-600 rounded-full border-2 border-gray-900 flex items-center justify-center text-white text-xs">
              +{likes.length - 4}
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handlePostSubmit = () => {
    handleCreatePost();
  };

  const handleSignOut = async () => {
    try {
      if (currentUser) {
        await axios.post(`${API_BASE}/api/community/logout`, null, {
          params: { user_id: currentUser.id }
        });
      }
    } catch (error) {
      console.error('Sign out API failed:', error);
    } finally {
      // Clear user data locally regardless of API success
      localStorage.removeItem('currentUser');
      setCurrentUser(null);
      setIsLoggedIn(false);
      await fetchCommunityStats(); // Refresh stats
      
      alert('You have been signed out successfully');
    }
  };

  const handleShowComments = async (post) => {
    setSelectedPost(post);
    setShowCommentsModal(true);
    
    try {
      const response = await axios.get(`${API_BASE}/api/community/posts/${post.id}/comments`);
      setPostComments(response.data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setPostComments([]);
    }
  };

  const handleAddComment = async () => {
    const commentText = commentTextareaRef.current?.value || '';
    
    if (!isLoggedIn || !commentText.trim() || !selectedPost) {
      return;
    }
    
    try {
      await axios.post(
        `${API_BASE}/api/community/posts/${selectedPost.id}/comments?user_id=${currentUser?.id || 'anonymous'}`,
        { content: commentText }
      );
      
      if (commentTextareaRef.current) {
        commentTextareaRef.current.value = '';
      }
      setHasCommentContent(false);
      
      // Refresh comments
      const response = await axios.get(`${API_BASE}/api/community/posts/${selectedPost.id}/comments`);
      setPostComments(response.data.comments || []);
      
      // Refresh posts to update comment count
      await fetchCommunityPosts();
    } catch (error) {
      console.error('Failed to add comment:', error);
      alert('Failed to add comment. Please try again.');
    }
  };

  const copyInviteLink = () => {
    const communityUrl = window.location.origin + '/community';
    navigator.clipboard.writeText(communityUrl).then(() => {
      alert('Community link copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = communityUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Community link copied to clipboard!');
    });
  };

  // Removed LoginModal and SignUpModal - using separate pages now

  const InviteModal = () => {
    const [inviteEmails, setInviteEmails] = useState('');
    const [inviteMessage, setInviteMessage] = useState('Join our ShansoConsult community to connect with business excellence professionals and share insights!');
    
    const handleInvite = () => {
      // In a real implementation, this would send invitation emails
      alert(`Invitations will be sent to: ${inviteEmails}`);
      setShowInviteModal(false);
      setInviteEmails('');
    };
    
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="bg-gray-900 border border-turquoise-500/30 rounded-16 p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlus size={32} className="text-turquoise-400" />
            </div>
            <h3 className="heading-3 text-white">Invite People</h3>
            <p className="text-gray-400 text-sm mt-2">Help grow our business excellence community</p>
          </div>
          
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email Addresses</label>
              <textarea
                placeholder="Enter email addresses separated by commas"
                value={inviteEmails}
                onChange={(e) => setInviteEmails(e.target.value)}
                rows="3"
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400"
                dir="ltr"
                style={{ direction: 'ltr' }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Personal Message</label>
              <textarea
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                rows="3"
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                dir="ltr"
                style={{ direction: 'ltr' }}
              />
            </div>
          </div>
          
          <div className="space-y-3">
            <div className="flex gap-3">
              <Button 
                className="btn-primary-enhanced flex-1"
                onClick={handleInvite}
                disabled={!inviteEmails.trim()}
              >
                Send Invites
              </Button>
              <Button 
                className="btn-secondary flex-1"
                onClick={() => setShowInviteModal(false)}
              >
                Cancel
              </Button>
            </div>
            
            <div className="border-t border-gray-700 pt-3">
              <Button 
                className="w-full bg-turquoise-500/20 hover:bg-turquoise-500/30 border border-turquoise-500/30 text-turquoise-400 font-medium transition-colors"
                onClick={copyInviteLink}
              >
                📋 Copy Community Link
              </Button>
              <p className="text-xs text-gray-500 text-center mt-2">
                Share this link to invite people directly
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const LinkModal = () => {
    const [linkUrl, setLinkUrl] = useState('');
    const [linkTitle, setLinkTitle] = useState('');
    
    const handleAddLink = () => {
      if (!linkUrl.trim()) {
        alert('Please enter a valid URL');
        return;
      }
      
      const linkText = linkTitle || linkUrl;
      const linkMarkdown = `[${linkTitle || 'Link'}](${linkUrl})`;
      setNewPostText(prev => prev + (prev ? '\n\n' : '') + linkMarkdown);
      
      setShowLinkModal(false);
      setLinkUrl('');
      setLinkTitle('');
    };
    
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="bg-gray-900 border border-turquoise-500/30 rounded-16 p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Link2 size={32} className="text-turquoise-400" />
            </div>
            <h3 className="heading-3 text-white mb-2">Add Link</h3>
            <p className="text-gray-400">Share a useful link with the community</p>
          </div>
          
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Link URL *</label>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-turquoise-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Link Title (optional)</label>
              <input
                type="text"
                value={linkTitle}
                onChange={(e) => setLinkTitle(e.target.value)}
                placeholder="Describe this link"
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-turquoise-400 focus:outline-none"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              className="btn-primary-enhanced flex-1"
              onClick={handleAddLink}
              disabled={!linkUrl.trim()}
            >
              Add Link
            </Button>
            <Button 
              className="btn-secondary flex-1"
              onClick={() => setShowLinkModal(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const PollModal = () => {
    const handleAddPoll = () => {
      if (!pollQuestion.trim()) {
        alert('Please enter a poll question');
        return;
      }
      
      const validOptions = pollOptions.filter(option => option.trim());
      if (validOptions.length < 2) {
        alert('Please provide at least 2 poll options');
        return;
      }
      
      const pollText = `**Poll: ${pollQuestion}**\n\n${validOptions.map((option, index) => `${index + 1}. ${option}`).join('\n')}`;
      setNewPostText(prev => prev + (prev ? '\n\n' : '') + pollText);
      
      setShowPollModal(false);
      setPollQuestion('');
      setPollOptions(['', '']);
    };
    
    const addPollOption = () => {
      setPollOptions(prev => [...prev, '']);
    };
    
    const updatePollOption = (index, value) => {
      setPollOptions(prev => prev.map((option, i) => i === index ? value : option));
    };
    
    const removePollOption = (index) => {
      if (pollOptions.length > 2) {
        setPollOptions(prev => prev.filter((_, i) => i !== index));
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="bg-gray-900 border border-turquoise-500/30 rounded-16 p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <BarChart3 size={32} className="text-turquoise-400" />
            </div>
            <h3 className="heading-3 text-white mb-2">Create Poll</h3>
            <p className="text-gray-400">Get community feedback with a poll</p>
          </div>
          
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Poll Question *</label>
              <input
                type="text"
                value={pollQuestion}
                onChange={(e) => setPollQuestion(e.target.value)}
                placeholder="What would you like to ask?"
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-turquoise-400 focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Poll Options</label>
              <div className="space-y-2">
                {pollOptions.map((option, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updatePollOption(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      className="flex-1 p-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-turquoise-400 focus:outline-none text-sm"
                    />
                    {pollOptions.length > 2 && (
                      <button
                        onClick={() => removePollOption(index)}
                        className="px-2 text-red-400 hover:text-red-300"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                
                {pollOptions.length < 6 && (
                  <button
                    onClick={addPollOption}
                    className="w-full p-2 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-turquoise-400 hover:border-turquoise-400 transition-colors text-sm"
                  >
                    + Add Option
                  </button>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              className="btn-primary-enhanced flex-1"
              onClick={handleAddPoll}
              disabled={!pollQuestion.trim() || pollOptions.filter(o => o.trim()).length < 2}
            >
              Create Poll
            </Button>
            <Button 
              className="btn-secondary flex-1"
              onClick={() => setShowPollModal(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const ReplyModal = () => {
    if (!selectedPostForReply) return null;
    
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="bg-gray-900 border border-turquoise-500/30 rounded-16 p-8 max-w-lg w-full mx-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Reply size={32} className="text-turquoise-400" />
            </div>
            <h3 className="heading-3 text-white mb-2">Reply to Post</h3>
            <p className="text-gray-400">Responding to @{selectedPostForReply.author_name || selectedPostForReply.author}</p>
          </div>
          
          {/* Original Post Preview */}
          <div className="mb-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-turquoise-500/20 rounded-full flex items-center justify-center">
                <span className="text-turquoise-400 text-sm font-semibold">
                  {(selectedPostForReply.author_name || selectedPostForReply.author)?.charAt(0) || 'A'}
                </span>
              </div>
              <span className="text-white font-medium">{selectedPostForReply.author_name || selectedPostForReply.author}</span>
            </div>
            <p className="text-gray-300 text-sm line-clamp-3">{selectedPostForReply.content}</p>
          </div>
          
          {/* Reply Input */}
          <div className="space-y-4 mb-6">
            <label className="block text-sm font-medium text-gray-300">Your Reply</label>
            <textarea
              ref={replyTextareaRef}
              defaultValue=""
              onKeyUp={(e) => setHasReplyContent(e.target.value.trim().length > 0)}
              placeholder="Write your reply..."
              rows="4"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 resize-none focus:outline-none focus:border-turquoise-400"
              dir="ltr"
              style={{ direction: 'ltr' }}
            />
            
            {/* Reply Image Upload */}
            <div className="flex items-center gap-2">
              <button 
                className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-300 text-sm transition-colors"
                onClick={() => document.getElementById('reply-image-upload').click()}
              >
                <Image size={16} />
                Add Image
              </button>
              <input 
                id="reply-image-upload" 
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={handleReplyImageUpload}
              />
            </div>
            
            {/* Reply Image Preview */}
            {replyImagePreview && (
              <div className="relative p-3 bg-gray-800 border border-turquoise-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="relative">
                    <img 
                      src={replyImagePreview} 
                      alt="Reply image preview" 
                      className="w-20 h-20 object-cover rounded-lg border border-gray-600"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-300 mb-1">Image attached</p>
                    <p className="text-xs text-gray-500">{replyImage?.name}</p>
                  </div>
                  <button 
                    onClick={removeReplyImagePreview}
                    className="text-red-400 hover:text-red-300 transition-colors p-1"
                    title="Remove image"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex gap-3">
            <button 
              className="btn-primary-enhanced flex-1 flex items-center justify-center gap-2"
              onClick={handleSubmitReply}
              disabled={!hasReplyContent}
            >
              <Reply size={16} />
              Post Reply
            </button>
            <button 
              className="btn-secondary flex-1"
              onClick={() => {
                setShowReplyModal(false);
                setSelectedPostForReply(null);
                setNewReply('');
                setReplyImage(null);
                setReplyImagePreview(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const RepliesModal = () => {
    if (!selectedPostReplies) return null;
    
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 border border-turquoise-500/30 rounded-16 p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
          
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-700">
            <div>
              <h3 className="heading-3 text-white mb-2">Post Replies</h3>
              <p className="text-gray-400">{postReplies.length} replies to this post</p>
            </div>
            <button 
              onClick={() => setShowRepliesModal(false)}
              className="text-gray-400 hover:text-white transition-colors p-2"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          
          {/* Original Post */}
          <div className="bg-gray-800/50 border border-turquoise-500/20 rounded-12 p-4 mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-turquoise-500/20 rounded-full flex items-center justify-center">
                <span className="text-turquoise-400 font-semibold">
                  {selectedPostReplies.author_name?.charAt(0) || 'A'}
                </span>
              </div>
              <div>
                <h4 className="font-semibold text-white">{selectedPostReplies.author_name}</h4>
                <p className="text-sm text-gray-400">{selectedPostReplies.author_role} • {new Date(selectedPostReplies.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            <h4 className="font-semibold text-white mb-2">{selectedPostReplies.title}</h4>
            <p className="text-gray-300">{selectedPostReplies.content}</p>
          </div>
          
          {/* Replies List */}
          <div className="space-y-4">
            <h4 className="font-semibold text-white mb-4">Replies ({postReplies.length})</h4>
            
            {postReplies.length === 0 ? (
              <div className="text-center py-8">
                <MessageCircle size={48} className="text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">No replies yet. Be the first to reply!</p>
              </div>
            ) : (
              postReplies.map((reply) => (
                <div key={reply.id} className={`border rounded-lg p-4 ${reply.is_best_answer ? 'bg-green-900/20 border-green-500/30' : 'bg-gray-800/30 border-gray-700'}`}>
                  
                  {/* Reply Header */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-turquoise-500/20 rounded-full flex items-center justify-center text-sm">
                      <span className="text-turquoise-400 font-semibold">
                        {reply.author_name?.charAt(0) || 'A'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h5 className="font-medium text-white">{reply.author_name}</h5>
                        {reply.is_best_answer && (
                          <span className="flex items-center gap-1 bg-green-500/20 text-green-400 px-2 py-1 rounded-full text-xs font-medium">
                            <Star size={12} />
                            Best Answer
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{reply.author_role} • {getTimeAgo(reply.created_at)}</p>
                    </div>
                  </div>
                  
                  {/* Reply Content */}
                  <p className="text-gray-300 mb-3">{reply.content}</p>
                  
                  {/* Reply Image */}
                  {reply.image && (
                    <div className="mb-3">
                      {reply.image.startsWith('data:') || reply.image.startsWith('http') ? (
                        <img 
                          src={reply.image} 
                          alt="Reply image"
                          className="max-w-full h-auto max-h-48 min-w-[100px] rounded-lg border border-gray-600 object-contain"
                          style={{ minHeight: '60px' }}
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-700 rounded-lg flex items-center justify-center text-2xl">
                          {reply.image}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Reply Actions */}
                  <div className="flex flex-col gap-3 pt-3 border-t border-gray-700">
                    <div className="flex items-center gap-4">
                      <button 
                        className="flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors text-sm"
                        onClick={() => handleLikeReply(reply.id)}
                      >
                        <Heart size={16} />
                        <span>{reply.likes || 0}</span>
                      </button>
                      
                      {/* Show user avatars for likes */}
                      <UserInteractionAvatars post={reply} />
                      
                      <button 
                        className="flex items-center gap-2 text-gray-400 hover:text-turquoise-400 transition-colors text-sm"
                        onClick={() => handleReplyToReply(reply)}
                      >
                        <Reply size={16} />
                        <span>Reply</span>
                      </button>
                      
                      {/* Post Owner Actions - Mark Best Answer */}
                      {currentUser && selectedPostReplies && currentUser.id === selectedPostReplies.author_id && (
                        <button 
                          className={`flex items-center gap-2 transition-colors text-sm ${reply.is_best_answer ? 'text-green-400 hover:text-green-300' : 'text-gray-400 hover:text-green-400'}`}
                          onClick={() => handleMarkBestAnswer(reply.id, selectedPostReplies.id)}
                          title={reply.is_best_answer ? "Unmark best answer" : "Mark as best answer"}
                        >
                          <Star size={16} className={reply.is_best_answer ? 'fill-current' : ''} />
                          <span>{reply.is_best_answer ? 'Best Answer' : 'Mark Best'}</span>
                        </button>
                      )}
                      
                      {/* Reply Owner Actions */}
                      {currentUser && currentUser.id === reply.author_id && (
                        <div className="flex items-center gap-2 ml-auto">
                          <button 
                            className="flex items-center gap-1 text-gray-400 hover:text-blue-400 transition-colors text-xs"
                            title="Edit your reply"
                          >
                            <Edit3 size={14} />
                            <span>Edit</span>
                          </button>
                          <button 
                            className="flex items-center gap-1 text-gray-400 hover:text-red-400 transition-colors text-xs"
                            onClick={() => handleDeleteOwnPost(reply.id)}
                            title="Delete your reply"
                          >
                            <Trash2 size={14} />
                            <span>Delete</span>
                          </button>
                        </div>
                      )}
                      
                      {/* Admin Actions */}
                      {currentUser?.role === 'admin' && currentUser.id !== reply.author_id && (
                        <button 
                          className="flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors text-sm ml-auto"
                          onClick={() => handleDeletePost(reply.id)}
                          title="Delete Reply (Admin Only)"
                        >
                          <Trash2 size={16} />
                          <span>Delete</span>
                        </button>
                      )}
                    </div>
                    
                    {/* User interaction avatars */}
                    {reply.likes > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="flex -space-x-1">
                          {Array.from({ length: Math.min(reply.likes, 3) }, (_, i) => (
                            <div 
                              key={i}
                              className="w-5 h-5 bg-gradient-to-br from-red-500 to-pink-500 rounded-full border-2 border-gray-900 flex items-center justify-center text-white text-xs font-semibold"
                            >
                              {String.fromCharCode(65 + i)}
                            </div>
                          ))}
                          {reply.likes > 3 && (
                            <div className="w-5 h-5 bg-gray-600 rounded-full border-2 border-gray-900 flex items-center justify-center text-white text-xs">
                              +{reply.likes - 3}
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">{reply.likes} likes</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Quick Reply Section */}
          {isLoggedIn && (
            <div className="mt-6 pt-6 border-t border-gray-700">
              <h5 className="font-medium text-white mb-3">Add a Reply</h5>
              <div className="flex gap-3">
                <button 
                  className="btn-primary-enhanced"
                  onClick={() => {
                    setSelectedPostForReply(selectedPostReplies);
                    setShowRepliesModal(false);
                    setShowReplyModal(true);
                  }}
                >
                  <Reply size={16} />
                  Write Reply
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => setShowRepliesModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const TrainingModal = () => {
    const [uploadType, setUploadType] = useState('video');
    const [uploadTitle, setUploadTitle] = useState('');
    const [uploadDescription, setUploadDescription] = useState('');
    const [uploadFile, setUploadFile] = useState(null);
    
    const handleUpload = () => {
      // In a real implementation, this would upload the file
      alert(`${uploadType} "${uploadTitle}" uploaded successfully!`);
      setShowTrainingModal(false);
      setUploadTitle('');
      setUploadDescription('');
      setUploadFile(null);
    };
    
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="bg-gray-900 border border-turquoise-500/30 rounded-16 p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Upload size={32} className="text-turquoise-400" />
            </div>
            <h3 className="heading-3 text-white">Upload Training Material</h3>
            <p className="text-gray-400 text-sm mt-2">Add video or document for the community</p>
          </div>
          
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
              <select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
              >
                <option value="video">Training Video</option>
                <option value="document">Document</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
              <input
                type="text"
                placeholder="Enter title"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <textarea
                placeholder="Enter description"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                rows="3"
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400"
                dir="ltr"
                style={{ direction: 'ltr' }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">File</label>
              <input
                type="file"
                accept={uploadType === 'video' ? 'video/*' : '.pdf,.doc,.docx,.ppt,.pptx'}
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:bg-turquoise-500 file:text-black"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              className="btn-primary-enhanced flex-1"
              onClick={handleUpload}
              disabled={!uploadTitle.trim() || !uploadFile}
            >
              Upload
            </Button>
            <Button 
              className="btn-secondary flex-1"
              onClick={() => setShowTrainingModal(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const CommentsModal = () => {
    if (!selectedPost) return null;

    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="bg-gray-900 border border-turquoise-500/30 rounded-16 p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="heading-3 text-white">Comments</h3>
            <button 
              onClick={() => {
                setShowCommentsModal(false);
                setSelectedPost(null);
                setPostComments([]);
              }}
              className="text-gray-400 hover:text-white text-xl"
            >
              ×
            </button>
          </div>

          {/* Post Preview */}
          <div className="bg-gray-800 rounded-lg p-4 mb-6">
            <p className="text-gray-300 whitespace-pre-wrap">{selectedPost.content}</p>
          </div>

          {/* Comments List */}
          <div className="space-y-4 mb-6">
            {postComments.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No comments yet. Be the first to comment!</p>
            ) : (
              postComments.map((comment) => (
                <div key={comment.id} className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-turquoise-500/20 rounded-full flex items-center justify-center text-sm">
                      👤
                    </div>
                    <div>
                      <span className="font-medium text-white">{comment.author_name}</span>
                      <span className="text-gray-400 text-xs ml-2">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-300">{comment.content}</p>
                </div>
              ))
            )}
          </div>

          {/* Add Comment */}
          {isLoggedIn ? (
            <div className="space-y-4">
              <textarea
                ref={commentTextareaRef}
                placeholder="Write a comment..."
                defaultValue=""
                onKeyUp={(e) => setHasCommentContent(e.target.value.trim().length > 0)}
                rows="3"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 resize-none focus:outline-none focus:border-turquoise-400"
                dir="ltr"
                style={{ direction: 'ltr' }}
              />
              <div className="flex gap-3">
                <Button 
                  className="btn-primary-enhanced"
                  onClick={handleAddComment}
                  disabled={!hasCommentContent}
                >
                  Add Comment
                </Button>
                <Button 
                  className="btn-secondary"
                  onClick={() => setNewComment('')}
                >
                  Clear
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-400 mb-4">Sign in to add comments</p>
              <Link to="/login">
                <Button className="btn-primary-enhanced">
                  Sign In
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="dark-container min-h-screen" style={{ paddingTop: '100px' }}>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Back Button */}
        <Link to="/">
          <Button className="btn-secondary mb-8">
            <ArrowLeft size={20} />
            Back to Home
          </Button>
        </Link>

        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="display-large mb-6">
            Business <span className="turquoise-text-gradient">Community</span>
          </h1>
          <p className="body-large text-gray-300 max-w-3xl mx-auto">
            Connect with fellow consultants, share insights, and grow together in the world of business excellence.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Left Side - Creative Image Section */}
          <div className="lg:col-span-3 hidden lg:block">
            <div className="sticky top-24 space-y-6">
              {/* Main Image Container with Creative Styling */}
              <div className="relative group">
                {/* Glow Effect Background */}
                <div className="absolute -inset-4 bg-gradient-to-br from-turquoise-500/20 via-cyan-500/20 to-purple-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                
                {/* Image Container */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900/50 to-gray-800/50 p-1">
                  <img 
                    src="/community-page.png"
                    alt="Business Community Network"
                    className="w-full h-auto rounded-xl transform group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>

              {/* Info Cards Below Image */}
              <div className="space-y-3">
                <div className="bg-gradient-to-r from-turquoise-500/10 to-cyan-500/10 border border-turquoise-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-turquoise-500/20 flex items-center justify-center">
                      <Users className="text-turquoise-400" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm">Active Members</h3>
                      <p className="text-xs text-gray-400">Join the conversation</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <MessageCircle className="text-purple-400" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm">Expert Discussions</h3>
                      <p className="text-xs text-gray-400">Share your insights</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <TrendingUp className="text-blue-400" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm">Growth & Learning</h3>
                      <p className="text-xs text-gray-400">Expand your knowledge</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <Award className="text-orange-400" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm">Industry Experts</h3>
                      <p className="text-xs text-gray-400">Learn from the best</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-6">
            {/* Enhanced Post Creation Area */}
            <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6 mb-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 bg-turquoise-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  {isLoggedIn ? '👤' : '🔒'}
                </div>
                <div className="flex-1">
                  <textarea
                    ref={newPostTextareaRef}
                    placeholder={isLoggedIn ? "Share your consulting insights..." : "Sign in to join the discussion"}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 resize-none"
                    rows="3"
                    defaultValue=""
                    onKeyUp={(e) => setHasPostContent(e.target.value.trim().length > 0)}
                    onClick={() => !isLoggedIn && handleLoginClick()}
                    dir="ltr"
                    style={{ direction: 'ltr' }}
                  />
                  
                  {/* Post Options */}
                  {isLoggedIn && (
                    <div className="mt-4">
                      <div className="flex flex-wrap items-center gap-2 mb-4">
                        <button 
                          className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-300 text-sm transition-colors"
                          onClick={() => document.getElementById('image-upload').click()}
                        >
                          <Image size={16} />
                          Add Image
                        </button>
                        <input 
                          id="image-upload" 
                          type="file" 
                          accept="image/*" 
                          className="hidden" 
                          onChange={handleImageUpload}
                        />
                        
                        <button 
                          className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-300 text-sm transition-colors"
                          onClick={() => setShowLinkModal(true)}
                        >
                          <Link2 size={16} />
                          Add Link
                        </button>
                        
                        <button 
                          className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-300 text-sm transition-colors"
                          onClick={() => setShowPollModal(true)}
                        >
                          <BarChart3 size={16} />
                          Poll
                        </button>
                        
                        <button 
                          className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-300 text-sm transition-colors"
                          onClick={() => setShowPostOptions(!showPostOptions)}
                        >
                          <Smile size={16} />
                          Emoji
                        </button>
                        
                        <select 
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 text-sm"
                        >
                          <option value="">Select Category</option>
                          {postCategories.map(category => (
                            <option key={category} value={category}>{category}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Emoji Picker */}
                      {showPostOptions && (
                        <div className="flex flex-wrap gap-2 p-3 bg-gray-800 rounded-lg mb-4">
                          {emojis.map(emoji => (
                            <button
                              key={emoji}
                              onClick={() => {
                                if (newPostTextareaRef.current) {
                                  const textarea = newPostTextareaRef.current;
                                  const cursorPos = textarea.selectionStart;
                                  const currentValue = textarea.value;
                                  const newValue = currentValue.slice(0, cursorPos) + emoji + currentValue.slice(cursorPos);
                                  textarea.value = newValue;
                                  // Set cursor after emoji
                                  textarea.selectionStart = textarea.selectionEnd = cursorPos + emoji.length;
                                  textarea.focus();
                                }
                                setShowPostOptions(false);
                              }}
                              className="text-xl hover:bg-gray-700 p-2 rounded transition-colors"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Selected Category Display */}
                      {selectedCategory && (
                        <div className="mb-4">
                          <span className="inline-block px-3 py-1 bg-turquoise-500/20 text-turquoise-400 rounded-full text-xs">
                            {selectedCategory}
                          </span>
                        </div>
                      )}
                      
                      {/* Image Preview */}
                      {imagePreview && (
                        <div className="relative p-3 bg-gray-800 border border-turquoise-500/30 rounded-lg mb-4">
                          <div className="flex items-start gap-3">
                            <div className="relative">
                              <img 
                                src={imagePreview} 
                                alt="Preview" 
                                className="w-20 h-20 object-cover rounded-lg border border-gray-600"
                              />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-300 mb-1">Image attached</p>
                              <p className="text-xs text-gray-500">{selectedImage?.name}</p>
                            </div>
                            <button 
                              onClick={removeImagePreview}
                              className="text-red-400 hover:text-red-300 transition-colors p-1"
                              title="Remove image"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Content Validation Status */}
                {isValidating && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-lg mb-4">
                    <div className="animate-spin">
                      <Loader2 size={16} className="text-yellow-400" />
                    </div>
                    <span className="text-sm text-yellow-400">Validating content...</span>
                  </div>
                )}
                
                {validationStatus && !validationStatus.is_valid && (
                  <div className="flex items-start gap-2 px-3 py-2 bg-red-500/20 border border-red-500/30 rounded-lg mb-4">
                    <AlertTriangle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-red-400">
                      <div className="font-medium mb-1">Content Policy Violation</div>
                      <ul className="list-disc list-inside space-y-0.5">
                        {validationStatus.violations.map((violation, index) => (
                          <li key={index}>{violation}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                
                {validationStatus && validationStatus.is_valid && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-500/20 border border-green-500/30 rounded-lg mb-4">
                    <CheckCircle size={16} className="text-green-400" />
                    <span className="text-sm text-green-400">Content approved for posting</span>
                  </div>
                )}
                
                <Button 
                  className="btn-primary-enhanced flex-shrink-0"
                  onClick={handlePostSubmit}
                  disabled={!isLoggedIn || !hasPostContent || isPosting || isValidating}
                >
                  {isValidating ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Validating...
                    </>
                  ) : isPosting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Posting...
                    </>
                  ) : (
                    <>
                      <Plus size={20} />
                      Post
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Categories removed - will be added later based on live posts */}

            {/* Posts Feed */}
            <div className="space-y-6">
              {communityPosts.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-24 h-24 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <MessageCircle size={48} className="text-turquoise-400" />
                  </div>
                  <h3 className="heading-3 text-white mb-4">Start the Conversation</h3>
                  <p className="body-medium text-gray-400 max-w-md mx-auto mb-6">
                    Be the first to share your insights, ask questions, or start a discussion in the ShansoConsult community.
                  </p>
                  {!isLoggedIn && (
                    <Link to="/signup">
                      <Button className="btn-primary-enhanced">
                        Join Community to Post
                      </Button>
                    </Link>
                  )}
                </div>
              ) : (
                communityPosts.filter(post => !post.parent_post_id).map((post) => (
                <div key={post.id} className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6 hover:border-turquoise-500/40 transition-all duration-300">
                  {/* Post Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-turquoise-500/20 rounded-full flex items-center justify-center text-xl">
                        👤
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">{post.author_name}</h4>
                        <p className="text-sm text-gray-400">{post.author_role} • {new Date(post.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    {post.category && (
                      <span className="px-3 py-1 bg-turquoise-500/20 text-turquoise-400 rounded-full text-xs font-medium">
                        {post.category}
                      </span>
                    )}
                  </div>

                  {/* Post Content */}
                  {editingPost && editingPost.id === post.id ? (
                    <div className="mb-4">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full p-3 bg-gray-800 border border-turquoise-500/30 rounded-lg text-white placeholder-gray-400 resize-none focus:outline-none focus:border-turquoise-400"
                        rows="4"
                        placeholder="Edit your post..."
                        dir="ltr"
                        style={{ direction: 'ltr' }}
                      />
                      <div className="flex items-center gap-3 mt-3">
                        <button 
                          onClick={handleSaveEdit}
                          className="flex items-center gap-2 bg-turquoise-500 hover:bg-turquoise-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                        >
                          <Check size={16} />
                          Save
                        </button>
                        <button 
                          onClick={() => setEditingPost(null)}
                          className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                        >
                          <X size={16} />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="cursor-pointer hover:bg-gray-800/30 rounded-lg p-2 -m-2 transition-colors"
                      onClick={() => handleShowAllReplies(post)}
                      title="Click to view all replies"
                    >
                      <p className="body-medium text-gray-300 mb-4 leading-relaxed whitespace-pre-wrap">{post.content}</p>
                    </div>
                  )}

                  {/* Post Image */}
                  {post.image && (
                    <div className="mb-4">
                      {post.image.startsWith('data:') || post.image.startsWith('http') ? (
                        <img 
                          src={post.image} 
                          alt="Post image"
                          className="max-w-full h-auto max-h-96 min-w-[120px] rounded-lg border border-gray-700 object-contain"
                          style={{ minHeight: '80px' }}
                        />
                      ) : (
                        <div className="w-full h-32 bg-gray-800 rounded-lg flex items-center justify-center text-4xl">
                          {post.image}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Post Meta Info */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                    <div className="flex items-center gap-4">
                      <span>{getTimeAgo(post.created_at)}</span>
                      {getLastCommentTime(post) && (
                        <span className="text-turquoise-400">{getLastCommentTime(post)}</span>
                      )}
                      {post.updated_at !== post.created_at && (
                        <span className="text-yellow-400">Edited</span>
                      )}
                    </div>
                    {pinnedPosts.includes(post.id) && (
                      <div className="flex items-center gap-1 text-yellow-400">
                        <Pin size={12} />
                        <span>Pinned</span>
                      </div>
                    )}
                  </div>

                  {/* Post Actions */}
                  <div className="flex flex-col gap-3 pt-4 border-t border-gray-800">
                    {/* Main Action Buttons */}
                    <div className="flex items-center gap-6">
                      <button 
                        className="flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors"
                        onClick={() => isLoggedIn ? handleLikePost(post.id) : handleLoginClick()}
                      >
                        <Heart size={18} />
                        <span>{post.likes || 0}</span>
                      </button>
                      <button 
                        className="flex items-center gap-2 text-gray-400 hover:text-turquoise-400 transition-colors"
                        onClick={() => handleShowAllReplies(post)}
                      >
                        <MessageCircle size={18} />
                        <span>{communityPosts.filter(p => p.parent_post_id === post.id).length}</span>
                      </button>
                      <button 
                        className="flex items-center gap-2 text-gray-400 hover:text-turquoise-400 transition-colors"
                        onClick={() => handleReplyToPost(post)}
                        title="Reply to this post"
                      >
                        <Reply size={18} />
                        <span className="text-xs">Reply</span>
                      </button>
                      <button 
                        className="flex items-center gap-2 text-gray-400 hover:text-green-400 transition-colors"
                        onClick={() => !isLoggedIn && handleLoginClick()}
                      >
                        <Share2 size={18} />
                        <span>{post.shares || 0}</span>
                      </button>
                      
                      {/* Post Owner Actions */}
                      {currentUser && currentUser.id === post.author_id && (
                        <div className="flex items-center gap-2 ml-auto">
                          <button 
                            className="flex items-center gap-1 text-gray-400 hover:text-blue-400 transition-colors text-xs"
                            onClick={() => handleEditPost(post)}
                            title="Edit your post"
                          >
                            <Edit3 size={14} />
                            <span>Edit</span>
                          </button>
                          <button 
                            className="flex items-center gap-1 text-gray-400 hover:text-red-400 transition-colors text-xs"
                            onClick={() => handleDeleteOwnPost(post.id)}
                            title="Delete your post"
                          >
                            <Trash2 size={14} />
                            <span>Delete</span>
                          </button>
                        </div>
                      )}
                      
                      {/* Pin Option - Available for post owners and admins */}
                      {currentUser && (currentUser.id === post.author_id || currentUser.role === 'admin') && (
                        <button 
                          className="flex items-center gap-1 text-gray-400 hover:text-yellow-400 transition-colors text-xs"
                          onClick={() => handlePinPost(post.id, post.author_id)}
                          title={pinnedPosts.includes(post.id) ? "Unpin post" : "Pin post"}
                        >
                          <Pin size={14} />
                          <span>{pinnedPosts.includes(post.id) ? 'Unpin' : 'Pin'}</span>
                        </button>
                      )}
                      
                      {/* Flag/Report Post - Available for all logged-in users except post owner */}
                      {currentUser && currentUser.id !== post.author_id && (
                        <button 
                          className="flex items-center gap-1 text-gray-400 hover:text-orange-400 transition-colors text-xs ml-auto"
                          onClick={() => {
                            setSelectedPostToFlag(post);
                            setShowFlagModal(true);
                          }}
                          title="Report this post"
                        >
                          <AlertTriangle size={14} />
                          <span>Report</span>
                        </button>
                      )}
                      
                      {/* Admin Delete for Others' Posts */}
                      {currentUser?.role === 'admin' && currentUser.id !== post.author_id && (
                        <div className="flex items-center gap-2 ml-auto">
                          <button 
                            className="flex items-center gap-1 text-gray-400 hover:text-red-400 transition-colors text-xs"
                            onClick={() => handleDeletePost(post.id)}
                            title="Delete Post (Admin Only)"
                          >
                            <Trash2 size={14} />
                            <span>Delete</span>
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Real User Interactions */}
                    <UserInteractionAvatars post={post} />
                  </div>
                </div>
                ))
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-3">
            {/* Community Info */}
            <div className="bg-gradient-to-br from-turquoise-500/10 to-blue-500/10 border border-turquoise-500/20 rounded-16 p-6 mb-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users size={32} className="text-turquoise-400" />
                </div>
                <h3 className="heading-3 text-white mb-2">ShansoConsult Community</h3>
                <p className="text-sm text-gray-400">Excellence • Innovation • Growth</p>
              </div>
              
              <div className="space-y-3 text-center">
                <div className="flex justify-between">
                  <span className="text-gray-400">Members</span>
                  <span className="text-turquoise-400 font-semibold">{(communityStats.members || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Posts Today</span>
                  <span className="text-turquoise-400 font-semibold">{communityStats.postsToday || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Active Now</span>
                  <span className="text-turquoise-400 font-semibold">{communityStats.activeNow || 0}</span>
                </div>
              </div>

              {isLoggedIn ? (
                <div className="mt-6 space-y-3">
                  <div className="text-center p-3 bg-turquoise-500/10 rounded-lg">
                    <p className="text-turquoise-400 font-medium">Welcome, {currentUser?.name}!</p>
                    <p className="text-gray-400 text-xs">{currentUser?.email}</p>
                    {currentUser?.role === 'admin' && (
                      <span className="inline-block mt-1 px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                        Administrator
                      </span>
                    )}
                  </div>
                  
                  {currentUser?.role === 'admin' && (
                    <Link to="/admin">
                      <Button className="w-full bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 text-purple-400 font-medium transition-colors">
                        🔐 Admin Panel
                      </Button>
                    </Link>
                  )}
                  
                  <Button 
                    className="w-full bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-400 font-medium transition-colors"
                    onClick={handleSignOut}
                  >
                    Sign Out
                  </Button>
                </div>
              ) : (
                <div className="mt-6 space-y-3">
                  <Link to="/signup">
                    <Button className="btn-primary-enhanced w-full">
                      Join Community
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button className="btn-secondary w-full">
                      Sign In
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Enhanced Signup Call-to-Action for Non-logged users */}
            {!isLoggedIn && (
              <div className="bg-gradient-to-r from-turquoise-500/20 to-blue-500/20 border border-turquoise-500/30 rounded-16 p-6 mb-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-turquoise-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users size={32} className="text-turquoise-400" />
                  </div>
                  <h3 className="heading-4 text-white mb-2">Ready to Join?</h3>
                  <p className="text-gray-300 text-sm mb-4">
                    Connect with 8+ business professionals and unlock exclusive content
                  </p>
                  <div className="space-y-2">
                    <Link to="/signup">
                      <Button className="btn-primary-enhanced w-full">
                        <UserPlus size={18} className="mr-2" />
                        Create Free Account
                      </Button>
                    </Link>
                    <Link to="/login">
                      <Button className="btn-secondary w-full text-sm">
                        Already a member? Sign In
                      </Button>
                    </Link>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-turquoise-500/20">
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div className="text-center">
                        <div className="text-turquoise-400 font-medium">✓ Free Access</div>
                        <div className="text-gray-400">No hidden costs</div>
                      </div>
                      <div className="text-center">
                        <div className="text-turquoise-400 font-medium">✓ Expert Network</div>
                        <div className="text-gray-400">Connect with pros</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Community Impact - Admin Only */}
            {currentUser?.role === 'admin' && (
              <div className="bg-gray-900/50 border border-turquoise-500/20 rounded-16 p-6 mb-6">
                <h4 className="heading-4 text-white mb-4">Community Impact</h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp size={20} className="text-green-400" />
                    <div>
                      <div className="text-sm font-semibold text-white">
                        {(communityStats.totalSavings || 0) > 0 ? `₹${((communityStats.totalSavings || 0) / 10000000).toFixed(1)}Cr` : '₹0'}
                      </div>
                      <div className="text-xs text-gray-400">Total Savings Generated</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Award size={20} className="text-yellow-400" />
                    <div>
                      <div className="text-sm font-semibold text-white">{(communityStats.successfulProjects || 0).toLocaleString()}</div>
                      <div className="text-xs text-gray-400">Successful Projects</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users size={20} className="text-blue-400" />
                    <div>
                      <div className="text-sm font-semibold text-white">
                        {(communityStats.successRate || 0) > 0 ? `${communityStats.successRate}%` : 'New'}
                      </div>
                      <div className="text-xs text-gray-400">Success Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Invite People Section */}
            <div className="bg-gradient-to-br from-blue-500/10 to-turquoise-500/10 border border-turquoise-500/20 rounded-16 p-6 mb-6">
              <div className="text-center mb-4">
                <div className="w-12 h-12 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <UserPlus size={24} className="text-turquoise-400" />
                </div>
                <h4 className="heading-4 text-white mb-2">Invite People</h4>
                <p className="text-sm text-gray-400 mb-4">
                  Grow our community by inviting colleagues and friends
                </p>
              </div>
              
              <div className="space-y-3">
                <button 
                  onClick={() => setShowInviteModal(true)}
                  className="w-full p-3 bg-turquoise-500/20 hover:bg-turquoise-500/30 border border-turquoise-500/30 rounded-lg text-turquoise-400 text-sm font-medium transition-colors"
                >
                  Send Invitation
                </button>
                <div className="text-xs text-gray-400 text-center">
                  Share the power of business excellence
                </div>
              </div>
            </div>

            {/* Sections removed as requested */}
          </div>
        </div>
      </div>

      {/* Footer section removed as requested */}

      {/* Login and Signup now use separate pages */}
      
      {/* Invite People Modal */}
      {showInviteModal && <InviteModal />}
      
      {/* Link Modal */}
      {showLinkModal && <LinkModal />}
      
      {/* Poll Modal */}
      {showPollModal && <PollModal />}
      
      {/* Reply Modal */}
      {showReplyModal && <ReplyModal />}
      
      {/* All Replies Modal */}
      {showRepliesModal && <RepliesModal />}
      
      {/* Training Upload Modal removed */}
      
      {/* Comments Modal */}
      {showCommentsModal && <CommentsModal />}
      {/* Flag/Report Post Modal */}
      {showFlagModal && selectedPostToFlag && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-700 rounded-16 p-6 max-w-md w-full">
            <h3 className="heading-3 text-white mb-4">Report Post</h3>
            <p className="text-gray-400 mb-4 text-sm">
              Help us maintain a safe community by reporting inappropriate content.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Reason for reporting</label>
                <select
                  value={flagReason}
                  onChange={(e) => setFlagReason(e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                >
                  <option value="">Select a reason...</option>
                  <option value="Spam">Spam</option>
                  <option value="Inappropriate Content">Inappropriate Content</option>
                  <option value="Harassment">Harassment</option>
                  <option value="Misinformation">Misinformation</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Additional details (optional)</label>
                <textarea
                  value={flagDetails}
                  onChange={(e) => setFlagDetails(e.target.value)}
                  placeholder="Provide more context about why you're reporting this post..."
                  rows="3"
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white"
                  dir="ltr"
                  style={{ direction: 'ltr' }}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleFlagPost}
                className="btn-primary-enhanced flex-1"
                disabled={!flagReason}
              >
                Submit Report
              </Button>
              <Button
                onClick={() => {
                  setShowFlagModal(false);
                  setSelectedPostToFlag(null);
                  setFlagReason('');
                  setFlagDetails('');
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Community;